import React from 'react';
import { Upload, Link as LinkIcon, CheckCircle2, Loader2 } from 'lucide-react';
import { TestVideo, TestSubmission } from './types';

interface VideoUploadCardProps {
  video: TestVideo;
  videoIdx: number;
  existingSubmissions: TestSubmission[];
  onUploadTypeChange: (videoIdx: number, type: 'file' | 'url') => void;
  onFileChange: (videoIdx: number, file: File | null) => void;
  onUrlChange: (videoIdx: number, url: string) => void;
  onTitleChange: (videoIdx: number, title: string) => void;
  onNotesChange: (videoIdx: number, notes: string) => void;
  onUpload: (videoIdx: number) => void;
  onResubmit: (videoIdx: number) => void;
}

function ScreeningStatus({ submission, videoIdx, onResubmit }: {
  submission: TestSubmission;
  videoIdx: number;
  onResubmit: (idx: number) => void;
}) {
  const statusConfig = {
    approved: { bg: 'bg-green-50 border-green-200', text: 'text-green-600', label: 'text-green-700', icon: '\u2713', title: 'Video Approved' },
    flagged: { bg: 'bg-red-50 border-red-200', text: 'text-red-600', label: 'text-red-700', icon: '\u26A0', title: 'Video Flagged - Action Required' },
    pending: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600', label: 'text-amber-700', icon: '\u23F3', title: 'Under Review' }
  };

  const config = statusConfig[submission.screening_status];

  return (
    <div className={`mb-4 p-4 rounded-lg border ${config.bg}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-lg ${config.text}`}>{config.icon}</span>
        <span className={`font-bold text-sm ${config.label}`}>{config.title}</span>
      </div>
      {submission.screening_status === 'flagged' && submission.screening_notes && (
        <p className="text-red-700 text-sm mt-2">
          <span className="font-semibold">Issue:</span> {submission.screening_notes}
        </p>
      )}
      {submission.screening_status === 'flagged' && (
        <button
          onClick={() => onResubmit(videoIdx)}
          className="mt-3 w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
        >
          Resubmit Video
        </button>
      )}
    </div>
  );
}

function UploadedState({ video, videoIdx, existingSubmissions, onResubmit }: {
  video: TestVideo;
  videoIdx: number;
  existingSubmissions: TestSubmission[];
  onResubmit: (idx: number) => void;
}) {
  const submission = existingSubmissions.find((s) => s.video_number === video.videoNumber);

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      {submission && (
        <ScreeningStatus submission={submission} videoIdx={videoIdx} onResubmit={onResubmit} />
      )}

      <div className="space-y-2 mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">Type:</span> {video.uploadType === 'file' ? 'File Upload' : 'URL'}
        </p>
        {video.title && (
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Title:</span> {video.title}
          </p>
        )}
        {video.notes && (
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Notes:</span> {video.notes}
          </p>
        )}
      </div>

      {submission?.screening_status === 'approved' && (
        <p className="text-sm text-gray-500 mb-4">
          This video has been approved and cannot be resubmitted.
        </p>
      )}
      {submission?.screening_status === 'pending' && (
        <p className="text-sm text-gray-500 mb-4">
          Your video is under review. You'll be notified once it's been reviewed.
        </p>
      )}
    </div>
  );
}

function UploadForm({ video, videoIdx, onUploadTypeChange, onFileChange, onUrlChange, onTitleChange, onNotesChange, onUpload }: {
  video: TestVideo;
  videoIdx: number;
  onUploadTypeChange: (idx: number, type: 'file' | 'url') => void;
  onFileChange: (idx: number, file: File | null) => void;
  onUrlChange: (idx: number, url: string) => void;
  onTitleChange: (idx: number, title: string) => void;
  onNotesChange: (idx: number, notes: string) => void;
  onUpload: (idx: number) => void;
}) {
  return (
    <>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Choose upload method:
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onUploadTypeChange(videoIdx, 'file')}
            className={`p-4 border-2 rounded-lg flex items-center justify-center transition-all ${
              video.uploadType === 'file'
                ? 'border-red-600 bg-red-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className="h-6 w-6 mr-2" />
            <span className="font-semibold">Upload File</span>
          </button>
          <button
            type="button"
            onClick={() => onUploadTypeChange(videoIdx, 'url')}
            className={`p-4 border-2 rounded-lg flex items-center justify-center transition-all ${
              video.uploadType === 'url'
                ? 'border-red-600 bg-red-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <LinkIcon className="h-6 w-6 mr-2" />
            <span className="font-semibold">Paste URL</span>
          </button>
        </div>
      </div>

      {video.uploadType === 'file' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Video File (Max 300 MB)
          </label>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => onFileChange(videoIdx, e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
          />
          {video.file && (
            <p className="mt-2 text-sm text-gray-600">
              Selected: {video.file.name} ({(video.file.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>
      )}

      {video.uploadType === 'url' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video URL (e.g., Google Drive, Dropbox, YouTube)
          </label>
          <input
            type="url"
            value={video.url}
            onChange={(e) => onUrlChange(videoIdx, e.target.value)}
            placeholder="https://..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
          />
        </div>
      )}

      {video.uploadType && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video Title (Optional)
            </label>
            <input
              type="text"
              value={video.title}
              onChange={(e) => onTitleChange(videoIdx, e.target.value)}
              placeholder={`Test Video ${video.videoNumber}`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={video.notes}
              onChange={(e) => onNotesChange(videoIdx, e.target.value)}
              placeholder="Any additional information about this video..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
            />
          </div>

          <button
            type="button"
            onClick={() => onUpload(videoIdx)}
            disabled={video.uploading}
            className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {video.uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                Upload Video {video.videoNumber}
              </>
            )}
          </button>
        </>
      )}
    </>
  );
}

export default function VideoUploadCard({
  video,
  videoIdx,
  existingSubmissions,
  onUploadTypeChange,
  onFileChange,
  onUrlChange,
  onTitleChange,
  onNotesChange,
  onUpload,
  onResubmit
}: VideoUploadCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Test Video {video.videoNumber}
        </h2>
        {video.uploaded && (
          <div className="flex items-center text-green-600">
            <CheckCircle2 className="h-6 w-6 mr-2" />
            <span className="font-semibold">Uploaded</span>
          </div>
        )}
      </div>

      {video.uploaded ? (
        <UploadedState
          video={video}
          videoIdx={videoIdx}
          existingSubmissions={existingSubmissions}
          onResubmit={onResubmit}
        />
      ) : (
        <UploadForm
          video={video}
          videoIdx={videoIdx}
          onUploadTypeChange={onUploadTypeChange}
          onFileChange={onFileChange}
          onUrlChange={onUrlChange}
          onTitleChange={onTitleChange}
          onNotesChange={onNotesChange}
          onUpload={onUpload}
        />
      )}
    </div>
  );
}
